import styled from '@emotion/styled';
import { Slider } from 'antd';
import { Howl } from 'howler';
import moment from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';
import loaderAnimation from 'src/public/animations/loader.json';
import pausePlayAnimation from 'src/public/animations/pause.json';
import IconBackward15 from 'src/public/img/icon-backward-15.svg';
import IconForward15 from 'src/public/img/icon-forward-15.svg';
import { color, font } from 'src/shared/styles/variables';
import { formatScondsToRoundedMinutes } from '../helpers/formatScondsToRoundedMinutes';

const Container = styled.div({
    width: 380,
});

const InfoContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const InfoImage = styled.img({
    width: 300,
});

const InfoTitle = styled.span(
    {
        marginTop: 50,
        color: color.ebonyClay,
        textAlign: 'center',
    },
    font.black(font.size.extraLarge),
);

const InfoDescription = styled.span(
    {
        marginTop: 10,
        color: color.ebonyClay,
    },
    font.book(font.size.medium),
);

const ProgressContainer = styled.div({
    marginTop: 80,
    display: 'flex',
    flexDirection: 'column',

    '.ant-slider': {
        margin: '0px 0px 9px 0px',
    },
    '.ant-slider:hover .ant-slider-track': {
        backgroundColor: color.cornflowerBlueDark,
    },
    '.ant-slider:hover .ant-slider-handle:not(.ant-tooltip-open)': {
        borderColor: color.white,
    },
    '.ant-slider-rail': {
        height: 6,
        backgroundColor: color.catskillWhite,
        borderRadius: 18,
    },
    '.ant-slider-track': {
        height: 6,
        backgroundColor: color.cornflowerBlueDark,
    },
    '.ant-slider-handle': {
        marginTop: -9,
        width: 24,
        height: 24,
        border: `6px solid ${color.white}`,
        backgroundColor: color.cornflowerBlueDark,
        boxShadow: '0px 1px 8px rgb(198 198 198 / 50%)',
    },
});

const ProgressLabelsContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
});

const ControlsContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    'img + img': {
        marginLeft: 32,
    },
});

const TimeLabels = styled.span({}, font.medium(font.size.small));

const PlayPauseContainer = styled.div({
    width: 58,
    height: 58,
    borderRadius: '50%',
    backgroundColor: color.ebonyClay,
    margin: '0px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const StyledLottie = styled(Lottie)<{ visible: boolean }>(({ visible }) => ({
    opacity: visible ? 1 : 0,
    transition: `opacity ${visible ? '.5s' : '.2s'} ease-in-out`,
}));

const PlayPause = styled(StyledLottie)({
    height: 30,
    filter: 'invert(1)',
    cursor: 'pointer',
});

const Loader = styled(StyledLottie)({
    position: 'absolute',
    height: 60,
});

const SeekByIcon = styled.img<{ loading: boolean }>(({ loading }) => ({
    opacity: loading ? 0.5 : 1,
    zIndex: 100,
    cursor: 'pointer',
}));

export interface AudioPlayerProps {
    audioSrc: string;
    imgSrc?: string;
    title?: string;
    onPlay?: VoidFunction;
    onListened?: VoidFunction;
}

interface AnimationProps {
    play: boolean;
    segments: [number, number];
    goTo: number | null;
}

export const AudioPlayer: FC<AudioPlayerProps> = ({
    audioSrc,
    imgSrc,
    title,
    onPlay,
    onListened,
}) => {
    const { t } = useTranslation();
    const [audioDuration, setAudioDuration] = useState(0);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);
    const [sliderValue, setSliderValue] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [animationProps, setAnimationProps] = useState<AnimationProps>({
        play: false,
        segments: [14, 28],
        goTo: 14,
    });
    const [info, setInfo] = useState({
        played: false,
        isListened: false,
    });

    const sound = useMemo(
        () =>
            new Howl({
                src: [audioSrc],
                html5: true,
                preload: true,
                onplay: () => setIsPlaying(true),
                onpause: () => setIsPlaying(false),
                onend: () => {
                    setAnimationProps(props => ({
                        ...props,
                        play: true,
                        goTo: null,
                    }));
                    setIsPlaying(false);
                },
            }),
        [audioSrc],
    );

    sound.once('load', () => {
        const duration = sound.duration();
        setAudioDuration(duration);
    });

    useEffect(() => {
        return () => sound.unload();
    }, [sound]);

    useEffect(() => {
        const interval = setInterval(() => {
            const seek = sound.seek();
            const duration = sound.duration();
            if (typeof seek === 'number') {
                // seek === 0 indicates that we've reached the end and we want to reset progress bar position
                if (isPlaying || seek === 0) {
                    setAudioCurrentTime(seek);
                    setSliderValue(seek);
                }
                if (seek / duration > 0.5 && !info.isListened) {
                    setInfo(prevInfo => ({ ...prevInfo, isListened: true }));
                    onListened?.();
                }
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [sound, isPlaying, info.isListened]);

    useEffect(() => {
        if (isPlaying && !info.played) {
            setInfo(prevInfo => ({ ...prevInfo, played: true }));
            onPlay?.();
        }
    }, [info.played, isPlaying]);

    const formatSecondToDisplayFormat = (seconds: number) =>
        moment.utc(seconds * 1000).format('m:ss');

    const handleChangeTime = (toSecond: number) => {
        sound.seek(toSecond);
        setSliderValue(toSecond);
    };

    const seekBy = seconds => {
        const currentSeek = sound.seek();
        handleChangeTime(currentSeek + seconds);
    };

    const handleAnimationComplete = () => {
        setAnimationProps(props => ({
            ...props,
            play: false,
            segments: isPlaying ? [0, 14] : [14, 28],
        }));
    };

    const handlePlayPauseClick = () => {
        if (!animationProps.play) {
            setAnimationProps(props => ({
                ...props,
                play: true,
                goTo: null,
            }));
            if (isPlaying) {
                sound.pause();
            } else {
                sound.play();
            }
        }
    };

    const playerState = sound.state();

    return (
        <Container>
            <InfoContainer>
                {imgSrc && <InfoImage src={imgSrc} alt="image for this audio" />}
                {title && <InfoTitle>{title}</InfoTitle>}
                <InfoDescription>
                    {t('shared:audio_player.description', {
                        minutes: formatScondsToRoundedMinutes(audioDuration),
                    })}
                </InfoDescription>
            </InfoContainer>
            <ProgressContainer>
                <Slider
                    min={0}
                    max={audioDuration}
                    defaultValue={0}
                    onChange={seconds => setSliderValue(seconds as number)}
                    tipFormatter={formatSecondToDisplayFormat}
                    value={sliderValue}
                    onAfterChange={handleChangeTime}
                />
                <ProgressLabelsContainer>
                    <TimeLabels>{formatSecondToDisplayFormat(audioCurrentTime)}</TimeLabels>
                    <TimeLabels>{formatSecondToDisplayFormat(audioDuration)}</TimeLabels>
                </ProgressLabelsContainer>
            </ProgressContainer>
            <ControlsContainer>
                <SeekByIcon
                    src={IconBackward15}
                    alt="backward by 15 seconds button"
                    onClick={() => seekBy(-15)}
                    loading={playerState !== 'loaded'}
                />
                <PlayPauseContainer>
                    <Loader
                        animationData={loaderAnimation}
                        loop
                        play={playerState !== 'loaded'}
                        speed={0.8}
                        visible={playerState !== 'loaded'}
                    />
                    <PlayPause
                        visible={playerState === 'loaded'}
                        animationData={pausePlayAnimation}
                        loop={false}
                        speed={1}
                        direction={1}
                        onComplete={handleAnimationComplete}
                        onClick={handlePlayPauseClick}
                        {...animationProps}
                    />
                </PlayPauseContainer>
                <SeekByIcon
                    src={IconForward15}
                    alt="forward by 15 seconds button"
                    onClick={() => seekBy(15)}
                    loading={sound.state() !== 'loaded'}
                />
            </ControlsContainer>
        </Container>
    );
};
